import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Card, 
  Statistic, 
  Row, 
  Col, 
  Tag, 
  Typography, 
  Space, 
  Tooltip,
  Badge,
  Avatar,
  Input,
  Select,
  Button,
  message,
  Skeleton,
  Empty,
  Alert,
  Progress,
  Divider
} from 'antd';
import { 
  UserOutlined, 
  LinkOutlined, 
  CheckCircleOutlined, 
  WarningOutlined, 
  DisconnectOutlined,
  SearchOutlined,
  TeamOutlined,
  GlobalOutlined,
  ApiOutlined,
  ReloadOutlined,
  PieChartOutlined,
  DownloadOutlined,
  FilterOutlined
} from '@ant-design/icons';
import { senseinsights_analytics_connections } from '../../api/admin.apis';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const SenseInsightsAnalyticsConnections = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [websiteFilter, setWebsiteFilter] = useState('all');

  // Fetch data from API
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await senseinsights_analytics_connections();
      if (response.data.status === 'success') {
        setData(response.data.data);
        setFilteredUsers(response.data.data.users);
        message.success('Data loaded successfully');
      } else {
        message.error('Failed to load data');
      }
    } catch (error) {
      message.error('Error fetching data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Apply filters
  useEffect(() => {
    if (!data) return;

    let filtered = [...data.users];

    // Apply search filter
    if (searchText) {
      filtered = filtered.filter(user => 
        user.email.toLowerCase().includes(searchText.toLowerCase()) ||
        (user.website_url && user.website_url.toLowerCase().includes(searchText.toLowerCase()))
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => user.status === statusFilter);
    }

    // Apply website filter
    if (websiteFilter !== 'all') {
      if (websiteFilter === 'with_website') {
        filtered = filtered.filter(user => user.website_url);
      } else if (websiteFilter === 'without_website') {
        filtered = filtered.filter(user => !user.website_url);
      }
    }

    setFilteredUsers(filtered);
  }, [searchText, statusFilter, websiteFilter, data]);

  // Status configuration
  const statusConfig = {
    CONNECTED: { 
      color: 'success', 
      icon: <CheckCircleOutlined />, 
      text: 'Connected',
      badge: 'success',
      progressColor: '#52c41a'
    },
    CONFIGURED_NO_DATA: { 
      color: 'warning', 
      icon: <WarningOutlined />, 
      text: 'Configured (No Data)',
      badge: 'warning',
      progressColor: '#faad14'
    },
    NOT_CONFIGURED: { 
      color: 'default', 
      icon: <DisconnectOutlined />, 
      text: 'Not Configured',
      badge: 'default',
      progressColor: '#8c8c8c'
    }
  };

  // Get status tag
  const getStatusTag = (status) => {
    const config = statusConfig[status] || statusConfig.NOT_CONFIGURED;
    return (
      <Tag icon={config.icon} color={config.color} style={{ padding: '4px 8px' }}>
        {config.text}
      </Tag>
    );
  };

  // Calculate statistics
  const getStatistics = () => {
    if (!data) return null;
    
    const total = data.summary.total_users;
    const connected = data.summary.connected;
    const configuredNoData = data.summary.configured_no_data;
    const notConfigured = data.summary.not_configured;
    
    return {
      total,
      connected,
      configuredNoData,
      notConfigured,
      connectedPercentage: (connected / total * 100).toFixed(1),
      configuredPercentage: (configuredNoData / total * 100).toFixed(1),
      notConfiguredPercentage: (notConfigured / total * 100).toFixed(1)
    };
  };

  // Table columns
  const columns = [
    {
      title: 'User Information',
      dataIndex: 'email',
      key: 'email',
      render: (text, record) => (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Space>
            <Avatar 
              icon={<UserOutlined />} 
              size="small" 
              style={{ 
                backgroundColor: record.status === 'CONNECTED' ? '#52c41a' : 
                               record.status === 'CONFIGURED_NO_DATA' ? '#faad14' : '#8c8c8c'
              }} 
            />
            <Text strong>{text}</Text>
          </Space>
          <Space wrap>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              ID: {record.user_id}
            </Text>
            {record.website_url && (
              <Tooltip title="Has website configured">
                <GlobalOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
              </Tooltip>
            )}
          </Space>
        </Space>
      ),
      sorter: (a, b) => a.email.localeCompare(b.email),
      width: '30%',
    },
    {
      title: 'Website',
      dataIndex: 'website_url',
      key: 'website',
      render: (url) => (
        url ? (
          <Space direction="vertical" size="small">
            <a 
              href={url.startsWith('http') ? url : `https://${url}`} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#1890ff' }}
            >
              <GlobalOutlined style={{ marginRight: 8 }} />
              {url.replace(/^https?:\/\//, '').substring(0, 30)}
              {url.length > 30 ? '...' : ''}
            </a>
            <Tag color="blue" style={{ fontSize: '10px' }}>
              {url.startsWith('https') ? 'HTTPS' : url.startsWith('http') ? 'HTTP' : 'Custom'}
            </Tag>
          </Space>
        ) : (
          <Text type="secondary" italic>No website configured</Text>
        )
      ),
      width: '35%',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
      filters: [
        { text: 'Connected', value: 'CONNECTED' },
        { text: 'Configured (No Data)', value: 'CONFIGURED_NO_DATA' },
        { text: 'Not Configured', value: 'NOT_CONFIGURED' },
      ],
      onFilter: (value, record) => record.status === value,
      width: '20%',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Tooltip title="View user details">
            <Button type="link" size="small" icon={<UserOutlined />}>
              Profile
            </Button>
          </Tooltip>
          {record.status === 'NOT_CONFIGURED' && (
            <Tooltip title="Set up analytics connection">
              <Button 
                type="link" 
                size="small" 
                icon={<ApiOutlined />}
                style={{ color: '#52c41a' }}
              >
                Configure
              </Button>
            </Tooltip>
          )}
          {record.status === 'CONFIGURED_NO_DATA' && (
            <Tooltip title="Check configuration">
              <Button 
                type="link" 
                size="small" 
                icon={<WarningOutlined />}
                style={{ color: '#faad14' }}
              >
                Troubleshoot
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
      width: '15%',
    },
  ];

  const stats = getStatistics();

  // Loading state
  if (loading && !data) {
    return (
      <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Space align="center">
            <Avatar 
              size={64} 
              icon={<ApiOutlined />} 
              style={{ backgroundColor: '#1890ff' }}
            />
            <div>
              <Title level={2} style={{ margin: 0 }}>
                SenseInsights Analytics
              </Title>
              <Text type="secondary">
                Manage and monitor analytics connections for all users
              </Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space>
            <Tooltip title="Refresh data">
              <Button 
                icon={<ReloadOutlined />} 
                onClick={fetchData}
                loading={loading}
              >
                Refresh
              </Button>
            </Tooltip>
            <Tooltip title="Export data">
              <Button icon={<DownloadOutlined />}>
                Export
              </Button>
            </Tooltip>
          </Space>
        </Col>
      </Row>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="Total Users"
              value={data?.summary.total_users || 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff', fontSize: '28px' }}
            />
            <div style={{ marginTop: 8 }}>
              <Progress 
                percent={100} 
                showInfo={false} 
                strokeColor="#1890ff"
                size="small"
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="Connected"
              value={data?.summary.connected || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a', fontSize: '28px' }}
              suffix={`/ ${data?.summary.total_users || 0}`}
            />
            <div style={{ marginTop: 8 }}>
              <Progress 
                percent={parseFloat(stats?.connectedPercentage || 0)} 
                strokeColor="#52c41a"
                size="small"
                format={(percent) => `${percent}%`}
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="Configured (No Data)"
              value={data?.summary.configured_no_data || 0}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#faad14', fontSize: '28px' }}
              suffix={`/ ${data?.summary.total_users || 0}`}
            />
            <div style={{ marginTop: 8 }}>
              <Progress 
                percent={parseFloat(stats?.configuredPercentage || 0)} 
                strokeColor="#faad14"
                size="small"
                format={(percent) => `${percent}%`}
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="Not Configured"
              value={data?.summary.not_configured || 0}
              prefix={<DisconnectOutlined />}
              valueStyle={{ color: '#8c8c8c', fontSize: '28px' }}
              suffix={`/ ${data?.summary.total_users || 0}`}
            />
            <div style={{ marginTop: 8 }}>
              <Progress 
                percent={parseFloat(stats?.notConfiguredPercentage || 0)} 
                strokeColor="#8c8c8c"
                size="small"
                format={(percent) => `${percent}%`}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Status Distribution */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8}>
            <Space direction="vertical">
              <Text strong>Status Distribution</Text>
              <PieChartOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
            </Space>
          </Col>
          <Col xs={24} md={16}>
            <Space wrap size="large">
              <Badge 
                status="success" 
                text={
                  <Space>
                    <span>Connected: {data?.summary.connected}</span>
                    <Text type="secondary">({stats?.connectedPercentage}%)</Text>
                  </Space>
                } 
              />
              <Badge 
                status="warning" 
                text={
                  <Space>
                    <span>Configured (No Data): {data?.summary.configured_no_data}</span>
                    <Text type="secondary">({stats?.configuredPercentage}%)</Text>
                  </Space>
                } 
              />
              <Badge 
                status="default" 
                text={
                  <Space>
                    <span>Not Configured: {data?.summary.not_configured}</span>
                    <Text type="secondary">({stats?.notConfiguredPercentage}%)</Text>
                  </Space>
                } 
              />
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Filters */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8}>
            <Space>
              <FilterOutlined />
              <Text strong>Filters</Text>
            </Space>
          </Col>
          <Col xs={24} md={16}>
            <Space wrap size="middle">
              <Search
                placeholder="Search by email or website"
                allowClear
                enterButton={<SearchOutlined />}
                onSearch={setSearchText}
                onChange={(e) => !e.target.value && setSearchText('')}
                style={{ width: 280 }}
              />
              <Select
                defaultValue="all"
                style={{ width: 180 }}
                onChange={setStatusFilter}
                placeholder="Status"
              >
                <Option value="all">All Status</Option>
                <Option value="CONNECTED">Connected</Option>
                <Option value="CONFIGURED_NO_DATA">Configured (No Data)</Option>
                <Option value="NOT_CONFIGURED">Not Configured</Option>
              </Select>
              <Select
                defaultValue="all"
                style={{ width: 180 }}
                onChange={setWebsiteFilter}
                placeholder="Website"
              >
                <Option value="all">All Websites</Option>
                <Option value="with_website">With Website</Option>
                <Option value="without_website">Without Website</Option>
              </Select>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Users Table */}
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <TeamOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                <Text strong>Users List</Text>
                <Tag color="blue">{filteredUsers.length} users</Tag>
                {(searchText || statusFilter !== 'all' || websiteFilter !== 'all') && (
                  <Tag color="geekblue" icon={<FilterOutlined />}>
                    Filtered
                  </Tag>
                )}
              </Space>
            </Col>
            <Col>
              <Text type="secondary">
                Last updated: {new Date().toLocaleString()}
              </Text>
            </Col>
          </Row>
        </div>

        {filteredUsers.length === 0 ? (
          <Empty 
            description="No users found matching the filters" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" onClick={() => {
              setSearchText('');
              setStatusFilter('all');
              setWebsiteFilter('all');
            }}>
              Clear Filters
            </Button>
          </Empty>
        ) : (
          <Table
            columns={columns}
            dataSource={filteredUsers}
            rowKey="user_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            loading={loading}
            expandable={{
              expandedRowRender: (record) => (
                <Card size="small" style={{ margin: '8px 0', background: '#fafafa' }}>
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Text type="secondary">User ID:</Text>
                      <div>
                        <Text code>{record.user_id}</Text>
                      </div>
                    </Col>
                    <Col span={8}>
                      <Text type="secondary">Email:</Text>
                      <div>
                        <Text>{record.email}</Text>
                      </div>
                    </Col>
                    <Col span={8}>
                      <Text type="secondary">Website URL:</Text>
                      <div>
                        {record.website_url ? (
                          <a href={record.website_url} target="_blank" rel="noopener noreferrer">
                            {record.website_url}
                          </a>
                        ) : (
                          <Text type="secondary">Not provided</Text>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card>
              ),
            }}
            scroll={{ x: 1000 }}
          />
        )}
      </Card>

      {/* Footer Summary */}
      <Card style={{ marginTop: '16px' }}>
        <Row gutter={16}>
          <Col span={24}>
            <Space wrap>
              <Text type="secondary">
                <CheckCircleOutlined style={{ color: '#52c41a' }} /> Connected users have active analytics tracking
              </Text>
              <Divider type="vertical" />
              <Text type="secondary">
                <WarningOutlined style={{ color: '#faad14' }} /> Configured but no data indicates setup issues
              </Text>
              <Divider type="vertical" />
              <Text type="secondary">
                <DisconnectOutlined style={{ color: '#8c8c8c' }} /> Not configured users need initial setup
              </Text>
            </Space>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default SenseInsightsAnalyticsConnections;